import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
    BasicMachineFunctionsService,
    t_FunctionConfig,
} from '../../../../services/apiHandler/basicMachineFunctions/basic-machine-functions.service';
import { DidDataRetrievalService, t_DataIdentifier, t_DataPointTimeSeries } from '../../../../services/apiHandler/did-data-retrieval/did-data-retrieval.service';
import * as PlotlyJS from 'plotly.js-dist-min';
import { initialPlotSettings } from 'src/app/services/helper/plotly-helper';
import { dataIdentifierEqual, findIndexOfIdentifierInDataPointTimeseries } from '../../../../services/helper/did-handling-helper';
import { initialPlotDataSetting } from '../../../../services/helper/plotly-helper';

@Component({
    selector: 'app-basic-view-graph',
    templateUrl: './basic-view-graph.component.html',
    styleUrls: ['./basic-view-graph.component.scss'],
})
export class BasicViewGraphComponent implements OnChanges {
    @Input() rawDataset?: t_DataPointTimeSeries[];
    @Input() functionConfig?: t_FunctionConfig;
    @Input() dataIdentifiersToDisplay?: t_DataIdentifier[];
    @Input() queryStarted?: boolean;
    public rawDatasetLoaded: boolean = false;
    public functionConfigLoaded: boolean = false;
    public graphDataLoaded: boolean = false;
    private graphDataSet: GraphDataset[] = [];
    private started: boolean = false;

    public plotSettings: PlotSetting;

    constructor(public functionService: BasicMachineFunctionsService, public dataRetrievalService: DidDataRetrievalService) {
        this.plotSettings = initialPlotSettings;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('rawDataset' in changes) {
            if(!this.started){
                this.dataRetrievalService.cleanDataPoints();
            }
            if (this.rawDataset && this.rawDataset.length > 0) {
                this.rawDatasetLoaded = true;
                this.updatePlotData();
            }
        }
        if ('functionConfig' in changes) {
            if (this.functionConfig && this.functionConfig.dataNames.length > 0) {
                this.functionConfigLoaded = true;
            }
        }

        if ('dataIdentifiersToDisplay' in changes) {
            let dataIdentifiersChecked: t_DataIdentifier[] = changes['dataIdentifiersToDisplay'].currentValue;
            this.resizePlotHeight(dataIdentifiersChecked.length);
            this.plotSettings.data = this.renderPlotData(dataIdentifiersChecked);
            this.focusAutorange();
        }

        if ('queryStarted' in changes) {
            if(changes['queryStarted'].currentValue) {
                this.focusAutorange();
                this.started = changes['queryStarted'].currentValue;
            } 
        }
    }

    public renderPlotData(dataIdentifierList: t_DataIdentifier[]): t_PlotData[] {
        // Filter raw dataset to only have checked values
        let filteredrawDataset: t_DataPointTimeSeries[] = this.filterDataPointTimeSeriesById(dataIdentifierList, this.rawDataset);

        // For each timeseries create a GraphTimeDataset and push it in the GraohTimeDataSets Array
        let graphTimeDataSets: GraphTimeDataset[] = [];
        dataIdentifierList.forEach((dataIdentifier) => {
            graphTimeDataSets.push(this.getGraphTimeDatasetFromId(dataIdentifier, filteredrawDataset));
        });        

        let plotDataset: t_PlotData[] = [];

        graphTimeDataSets.forEach((datapoint) => {
            let plotData: t_PlotData = JSON.parse(JSON.stringify(initialPlotDataSetting));
            plotData.x = datapoint.times;
            plotData.y = datapoint.values;
            plotData.identifier = datapoint.identifier;
            plotData.name = datapoint.name;
            plotDataset.push(plotData);
        });

        if (plotDataset.length > 0) {
            this.graphDataLoaded = true;
        } else {
            this.graphDataLoaded = false;
        }

        return plotDataset;
    }

    public updatePlotData() {
        let dataIdentifiers: t_DataIdentifier[];
        if (this.dataIdentifiersToDisplay == undefined) {
            dataIdentifiers = [];
        } else {
            dataIdentifiers = this.dataIdentifiersToDisplay;
        }
        this.plotSettings.data = this.renderPlotData(dataIdentifiers);
    }

    /**
     * ### Filter a given Datapoint time series
     * **by List of identifiers**
     * @param { t_DataIdentifier[]} dataIdentifierList - List of data identifiers according to which the rawDataSet is to be filtered
     * @param { t_DataPointTimeSeries[]} dataset? - Dataset which will be filtered
     * @return {t_DataPointTimeSeries[]}  - The filtered dataset started from the rawDataset with only the values for the given dataIdentifiers
     */
    private filterDataPointTimeSeriesById(
        dataIdentifierList: t_DataIdentifier[],
        dataset?: t_DataPointTimeSeries[]
    ): t_DataPointTimeSeries[] {
        // Catch if IdentifierList is empty
        if (dataset == undefined) {
            console.warn('No raw data provided as Input found in Basic Graph Component');
            return [];
        }

        // Compare IdentifierList with every element of the rawDataset and build a filtered dataset with the found data
        let filteredDataset: t_DataPointTimeSeries[] = dataset.filter((dataPointTimeSeries: t_DataPointTimeSeries) => {
            return dataIdentifierEqual(dataPointTimeSeries.identifier, dataIdentifierList);
        });
        return filteredDataset;
    }

    private getGraphTimeDatasetFromId(identifier: t_DataIdentifier, dataset: t_DataPointTimeSeries[]) {
        let datasetIndex: number = findIndexOfIdentifierInDataPointTimeseries(identifier, dataset);
        let graphTimeDataset: GraphTimeDataset = {
            times: [],
            values: [],
            name: '',
            identifier: {
                ecu_id: 0,
                data_id: 0,
            },
        };

        graphTimeDataset.name = this.functionService.getDataNameOfIdentifier(identifier) + ' (' + this.functionService.getUnitOfIdentifier(identifier) + ')';
        graphTimeDataset.identifier = identifier;

        dataset[datasetIndex].data?.forEach((datapoint) => {
            let timeFromStart: number = this.getTimeFromStart(datapoint.timestamp);
            graphTimeDataset.times.push(timeFromStart);            
            let normalizedValue: number = this.normalizedValue(
                datapoint.value[0], 
                this.functionService.didConfigConnection.getDidInformation(identifier)!.conversionFactor,
                this.functionService.didConfigConnection.getDidInformation(identifier)!.conversionOffset
            );
            graphTimeDataset.values.push(normalizedValue);
        });

        return graphTimeDataset;
    }

    private getTimeFromStart(timestamp: Date) {
        let timeFromStart: number;
        let startTime = this.dataRetrievalService.getStartTime();
        timeFromStart = (Number(timestamp) - startTime) /1000; 
        return timeFromStart;
    }

    private normalizedValue( value: number, factor: number, offset: number) {
        if(factor == 0) {
            factor = 1;
        }
        let res: number = (value * factor) + offset;
        res = parseFloat(res.toFixed(1));
        return res;
    }

    public resizePlotHeight(numberOfDisplayedGraphs: number) {
        let initialHeight = 600; // From initialPlotSettings.layout.height;
        this.plotSettings.layout.height = initialHeight + numberOfDisplayedGraphs * 25;
    }

    // Get separate Arrays for time and value digits of an specific datapoint specified by data identifier
    private getSeparateArraysFromTimeSeries(identifier: t_DataIdentifier): GraphDataset {
        let datasetIndex: number = this.getRawDatasetIndexFromIdentifier(identifier);
        let name: string = this.functionService.getDataNameOfIdentifier(identifier);
        let times: number[] = [];
        let values: number[] = [];

        this.rawDataset![datasetIndex].data?.forEach((dataPoint) => {
            times.push(new Date(dataPoint.timestamp).getTime());
            values.push(dataPoint.value[0]);
        });

        return { times, values, name };
    }

    // Return the index of the timeseries of an given identifier
    private getRawDatasetIndexFromIdentifier(id: t_DataIdentifier): number {
        let index: number = -1;
        if (this.rawDatasetLoaded == true) {
            index = this.rawDataset!.findIndex((series) => {
                return series.identifier.ecu_id == id.ecu_id && series.identifier.data_id == id.data_id;
            });
        }
        return index;
    }

    private focusAutorange(){
        this.plotSettings.layout.xaxis.autorange = true;
        this.plotSettings.layout.yaxis.autorange = true;
    }

}


export type PlotSetting = {
    data: t_PlotData[];
    layout: {
        height: number;
        xaxis: {
            type: string;
            tickmode: string;
            tickangle: number;
            nticks: number;
            autorange: boolean;
            rangeslider: {};
        };
        yaxis: {
            type: string,
            tickmode: string,
            rangemode: string,
            autorange: boolean,
            fixedrange: boolean,
        };
        margin: {};
        showlegend: boolean;
        legend: {};
        colorway: string[];
    };
    config: {};
    style: {};
};

export type t_PlotData = {
    x: number[];
    y: number[];
    type: PlotlyJS.AxisType;
    mode: string;
    name: string;
    /*line: {
        color: string;
    };*/
    identifier: {
        ecu_id: number;
        data_id: number;
    };
};

type GraphDataset = {
    times: number[];
    values: number[];
    name: string;
};

type GraphTimeDataset = {
    times: number[];
    values: number[];
    name: string;
    identifier: t_DataIdentifier;
};
